import { StateCreator } from 'zustand'
import { MedplumClient } from '@medplum/core'
import dayjs, { Dayjs } from 'dayjs'
import { Condition } from '@medplum/fhirtypes'
import {
  BaseConsentFragment,
  BaseOrganizationFragment,
  BasePatientFragment,
  PatientData_CareTeamListFragment,
  PatientData_GetPatientQuery,
  PatientData_PatientCareDataFragment
} from 'generated/graphql'

import { ACTIVE_CONSENT_STATUSES, ConsentStatus } from 'fhir/Consent/constants'
import { ProviderOrderPreferences } from 'fhir/Organization/helpers'
import { BaseEpisodeOfCareWithConsentFragment } from 'fhir/Patient/helpers'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { fetchPatientDataPatient } from 'services/patient/PatientFetchService'
import { RootSlice } from './rootType'

import { formatPatientData } from './helpers/patient.helpers'

export type PatientListSchema = PatientSchema[]
export interface PatientSlice {
  patient: {
    list: PatientListSchema
    idToPatientMapping: Record<string, PatientSchema>
    isLoading: boolean
    careLocations: { careLocation1: string | null; careLocation2: string | null } | null
    parsePatient: (patient: PatientData_GetPatientQuery['Patient']) => void
    getPatient: (
      medplum: MedplumClient,
      id: string,
      consentStatuses?: readonly ConsentStatus[],
      useApi?: boolean
    ) => Promise<PatientSchema | null>
    errorMessage: object | string | null
    update: (patients: Array<PatientData_PatientCareDataFragment | null> | null) => void
  }
}

export interface PatientSchema {
  id: string
  name: string
  dateOfBirth: Dayjs | null
  gender: string | null
  mrn: string
  ssn: string | null
  addressLine: string | null
  addressLine2: string | null
  addressCity: string | null
  addressState: string | null
  addressPostalCode: string | null
  addressDisplay: string | null
  preferredName: string | null
  patientStatus: string
  admissionStatus: string
  primaryLanguage: string | null
  ethnicity: string | null
  conditions: Partial<Condition>[]
  sex: string | null
  careLocation1: string | null
  careLocation2: string | null
  primaryCareProvider: BaseOrganizationFragment | null
  providerOrderPreferences: ProviderOrderPreferences[]
  authorizations: BaseConsentFragment[]
  activeAuthorization: BaseConsentFragment | null
  currentCertPeriods: BaseEpisodeOfCareWithConsentFragment[]
  careTeam: PatientData_CareTeamListFragment[]
  caregiverTimezone: string
  serviceTypes: ServiceTypeName[] | null
  managingOrganization: BasePatientFragment['managingOrganization'] | null
  careTeamId: string | null
  currentEpisodeOfCare?: BaseEpisodeOfCareWithConsentFragment | null
  nextEpisodeOfCare?: BaseEpisodeOfCareWithConsentFragment | null
  currentCertPeriod?: { start: dayjs.Dayjs; end: dayjs.Dayjs } | null
}

export const createPatientSlice: StateCreator<
  RootSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  PatientSlice
> = (set) => ({
  patient: {
    list: [],
    idToPatientMapping: {},
    isLoading: false,
    errorMessage: null,
    patientData: null,
    careLocations: null,
    parsePatient: (patient: PatientData_GetPatientQuery['Patient']) => {
      if (!patient) return
      const formattedData = formatPatientData(patient)
      if (formattedData) {
        set((state) => {
          state.patient.idToPatientMapping[formattedData.id as string] = formattedData
        })
      }
    },
    // TODO (Jason): refactor this
    getPatient: async (medplum, id, consentStatuses = ACTIVE_CONSENT_STATUSES, useApi = false) => {
      try {
        set((state) => {
          state.patient.errorMessage = null
          state.patient.isLoading = true
        })

        const consentStatusFilter = `status in ${consentStatuses.join(',')}`

        const patientData = await fetchPatientDataPatient(medplum, id, useApi, consentStatusFilter)

        const patient = patientData?.Patient

        if (!patient?.id) {
          throw new Error('Patient ID not found')
        }
        const patientId = patient.id

        let formattedData: PatientSchema | undefined

        if (patient) {
          formattedData = formatPatientData(patient)
        }
        set((state) => {
          if (formattedData) {
            state.patient.idToPatientMapping[patientId] = formattedData
          }
          state.patient.errorMessage = null
        })

        return formattedData ?? null
      } catch (error) {
        set((state) => {
          state.patient.errorMessage = error?.message || 'Error getting patient data.'
        })

        return null
      } finally {
        set((state) => {
          state.patient.isLoading = false
        })
      }
    },
    update: (patients: Array<PatientData_PatientCareDataFragment | null> | null) => {
      if (!patients) return
      const parsedMapping = patients.reduce((acc, curr) => {
        if (!curr) return acc
        const formattedData = formatPatientData(curr)
        if (formattedData) {
          acc[formattedData.id as string] = formattedData
        }
        return acc
      }, {})

      set((state) => {
        state.patient.idToPatientMapping = {
          ...state.patient.idToPatientMapping,
          ...parsedMapping
        }
      })
    }
  }
})
